import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close,
  Add,
  Remove,
  TableChart
} from '@mui/icons-material';

interface AdvancedTableEditorProps {
  open: boolean;
  onClose: () => void;
  onTableInsert: (tableHtml: string) => void;
}

interface TableData {
  rows: number;
  cols: number;
  hasHeader: boolean;
  hasBorder: boolean;
}

const AdvancedTableEditor: React.FC<AdvancedTableEditorProps> = ({
  open,
  onClose,
  onTableInsert
}) => {
  const [tableData, setTableData] = useState<TableData>({
    rows: 3,
    cols: 3,
    hasHeader: true,
    hasBorder: true
  });

  const generateTableHtml = (): string => {
    const borderStyle = tableData.hasBorder
      ? 'border: 1px solid #ddd; border-collapse: collapse; border-spacing: 0;'
      : 'border-collapse: collapse; border-spacing: 0;';

    const cellStyle = tableData.hasBorder
      ? 'border: 1px solid #ddd; padding: 0; font-size: 12px;'
      : 'padding: 0; font-size: 12px;';

    let html = `<table cellpadding="0" cellspacing="0" style="${borderStyle} width: 100%; margin: 10px 0; table-layout: fixed;">`;

    if (tableData.hasHeader) {
      html += '<thead><tr>';
      for (let colIndex = 0; colIndex < tableData.cols; colIndex++) {
        const headerStyle = 'background-color: #e9e9e9; font-weight: bold; font-size: 12px;';
        html += `<th style="${cellStyle} ${headerStyle}">Coluna ${colIndex + 1}</th>`;
      }
      html += '</tr></thead>';
    }

    html += '<tbody>';
    for (let rowIndex = 0; rowIndex < tableData.rows; rowIndex++) {
      html += '<tr>';
      for (let colIndex = 0; colIndex < tableData.cols; colIndex++) {
        html += `<td style="${cellStyle}">&nbsp;</td>`;
      }
      html += '</tr>';
    }
    html += '</tbody>';

    html += '</table>';
    return html;
  };

  const handleInsert = () => {
    const tableHtml = generateTableHtml();
    onTableInsert(tableHtml);
    handleClose();
  };

  const handleClose = () => {
    setTableData({ rows: 3, cols: 3, hasHeader: true, hasBorder: true });
    onClose();
  };

  const totalRows = tableData.rows + (tableData.hasHeader ? 1 : 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={{ zIndex: 10000 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <TableChart sx={{ fontSize: 18 }} />
          <Typography variant="subtitle1" fontWeight={600} fontSize="0.9rem">Inserir Tabela</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Linhas e Colunas */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" fontWeight={600} sx={{ minWidth: 45 }}>Linhas:</Typography>
              <IconButton
                size="small"
                onClick={() => setTableData(prev => ({ ...prev, rows: Math.max(1, prev.rows - 1) }))}
                disabled={tableData.rows <= 1}
                sx={{ p: '2px' }}
              >
                <Remove sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 18, textAlign: 'center' }}>
                {tableData.rows}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setTableData(prev => ({ ...prev, rows: Math.min(15, prev.rows + 1) }))}
                disabled={tableData.rows >= 15}
                sx={{ p: '2px' }}
              >
                <Add sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" fontWeight={600} sx={{ minWidth: 52 }}>Colunas:</Typography>
              <IconButton
                size="small"
                onClick={() => setTableData(prev => ({ ...prev, cols: Math.max(1, prev.cols - 1) }))}
                disabled={tableData.cols <= 1}
                sx={{ p: '2px' }}
              >
                <Remove sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 18, textAlign: 'center' }}>
                {tableData.cols}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setTableData(prev => ({ ...prev, cols: Math.min(15, prev.cols + 1) }))}
                disabled={tableData.cols >= 15}
                sx={{ p: '2px' }}
              >
                <Add sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Opções */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tableData.hasHeader}
                  onChange={(e) => setTableData(prev => ({ ...prev, hasHeader: e.target.checked }))}
                  size="small"
                />
              }
              label={<Typography variant="caption">Cabeçalho</Typography>}
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={tableData.hasBorder}
                  onChange={(e) => setTableData(prev => ({ ...prev, hasBorder: e.target.checked }))}
                  size="small"
                />
              }
              label={<Typography variant="caption">Bordas</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>

          
          {/* Preview */}
          <Box>
            <Typography variant="caption" fontWeight={600} gutterBottom display="block">Preview:</Typography>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 0.5,
                p: 1,
                backgroundColor: 'white',
                maxHeight: 200,
                overflow: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: generateTableHtml() }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 1.5, py: 0.8 }}>
        <Button onClick={handleClose} size="small" sx={{ fontSize: '0.78rem' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleInsert}
          variant="contained"
          size="small"
          startIcon={<TableChart sx={{ fontSize: 16 }} />}
          sx={{ fontSize: '0.78rem' }}
        >
          Inserir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedTableEditor; 